// src/components/support/UserContextPanel.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import userService from '../../services/userService';
import { format, parseISO, isFuture, isPast } from 'date-fns';
import api from '../../api/apiClient';
import { useUI } from '../../context/UIContext';
import { PersonIcon, EmailIcon, CalendarIcon, CheckCircleIcon, AlertIcon, PhoneIcon } from '../common/Icons';
import { Appointment } from '../../types/appointment';
import { publicAppointmentRef } from '../../utils/publicRefs';
import '../../styles/UserContextPanel.css';

interface UserContextPanelProps {
  userId: string;
  onClose?: () => void;
}

interface UserInfo {
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  userType: 'client' | 'stylist' | 'admin';
  emailVerified: boolean;
  profilePhoto?: string;
  location?: {
    city?: string;
    state?: string;
  };
  createdAt: string;
}

const UserContextPanel: React.FC<UserContextPanelProps> = ({ userId, onClose }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useUI();
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserContext = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user info
        const user = await userService.getUserById(userId);
        setUserInfo(user);

        // Fetch user's appointments (if client or stylist)
        if (user.userType === 'client' || user.userType === 'stylist') {
          try {
            // For support agents, we can fetch appointments by user ID
            // Using a query parameter to filter by client or stylist
            const queryParam = user.userType === 'client' ? `client=${userId}` : `stylist=${userId}`;
            const response = await api.get(`/appointments?${queryParam}`);
            if (response.data?.data) {
              setAppointments(response.data.data.slice(0, 10)); // Show last 10
            }
          } catch (err) {
            console.error('Error fetching appointments:', err);
            // Don't show error for appointments, just continue
          }
        }
      } catch (err: any) {
        console.error('Error fetching user context:', err);
        setError(err.message || 'Failed to load user information');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserContext();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="user-context-panel">
        <div className="user-context-panel__loading">
          <div className="spinner"></div>
          <p>Loading user information...</p>
        </div>
      </div>
    );
  }

  if (error || !userInfo) {
    return (
      <div className="user-context-panel">
        <div className="user-context-panel__error">
          <AlertIcon size={24} />
          <p>{error || 'User not found'}</p>
        </div>
      </div>
    );
  }

  const getStatus = (apt: Appointment) => apt.status;
  const upcomingAppointments = appointments.filter(apt => {
    const aptDate = parseISO(apt.dateTime);
    return isFuture(aptDate) && ['pending', 'confirmed'].includes(getStatus(apt));
  });

  const pastAppointments = appointments.filter(apt => {
    const aptDate = parseISO(apt.dateTime);
    const s = getStatus(apt);
    return isPast(aptDate) || s === 'completed' || s === 'expired';
  });

  // Handle verify email action
  const handleVerifyEmail = async () => {
    if (!userInfo) return;
    
    try {
      setActionLoading('verify-email');
      await userService.verifyUserEmail(userId);
      // Refresh user info
      const updatedUser = await userService.getUserById(userId);
      setUserInfo(updatedUser);
      showSnackbar('Email verified successfully!', 'success');
    } catch (err: any) {
      console.error('Error verifying email:', err);
      showSnackbar(err.message || 'Failed to verify email', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle view profile action
  const handleViewProfile = () => {
    if (!userInfo) return;
    // For support agents viewing another user's profile, we show the info in the context panel
    // The user info is already displayed in this panel, so we just scroll to top or show a message
    showSnackbar(`Viewing profile information for ${userInfo.name}`, 'info');
    // Scroll to top of panel
    const panel = document.querySelector('.user-context-panel');
    if (panel) {
      panel.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle view appointments action
  const handleViewAppointments = () => {
    if (!userInfo) return;
    // Appointments are already shown in the panel, so we scroll to that section
    showSnackbar(`Showing appointments for ${userInfo.name}`, 'info');
    // Scroll to appointments section
    const appointmentsSection = document.querySelector('.user-context-panel__appointments-summary');
    if (appointmentsSection) {
      appointmentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="user-context-panel">
      {onClose && (
        <div className="user-context-panel__header">
          <h3>User Information</h3>
          <button onClick={onClose} className="user-context-panel__close">
            ×
          </button>
        </div>
      )}

      {/* User Profile Section */}
      <div className="user-context-panel__section">
        <div className="user-context-panel__profile">
          <div className="user-context-panel__avatar">
            {userInfo.profilePhoto ? (
              <img src={userInfo.profilePhoto} alt={userInfo.name} />
            ) : (
              <span>{userInfo.name.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="user-context-panel__profile-info">
            <h4>{userInfo.name}</h4>
            <span className={`user-context-panel__badge user-context-panel__badge--${userInfo.userType}`}>
              {userInfo.userType}
            </span>
          </div>
        </div>

        <div className="user-context-panel__details">
          <div className="user-context-panel__detail-item">
            <EmailIcon size={16} />
            <span>{userInfo.email}</span>
            {userInfo.emailVerified ? (
              <CheckCircleIcon size={16} color="#10B981" />
            ) : (
              <span title="Email not verified">
                <AlertIcon size={16} color="#F59E0B" />
              </span>
            )}
          </div>
          {userInfo.phoneNumber && (
            <div className="user-context-panel__detail-item">
              <PhoneIcon size={16} />
              <span>{userInfo.phoneNumber}</span>
            </div>
          )}
          {userInfo.location && (userInfo.location.city || userInfo.location.state) && (
            <div className="user-context-panel__detail-item">
              <PersonIcon size={16} />
              <span>
                {[userInfo.location.city, userInfo.location.state].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Account Status */}
      <div className="user-context-panel__section">
        <h5 className="user-context-panel__section-title">Account Status</h5>
        <div className="user-context-panel__status-grid">
          <div className="user-context-panel__status-item">
            <span className="user-context-panel__status-label">Email Verified</span>
            <span className={`user-context-panel__status-value ${userInfo.emailVerified ? 'verified' : 'unverified'}`}>
              {userInfo.emailVerified ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="user-context-panel__status-item">
            <span className="user-context-panel__status-label">Member Since</span>
            <span className="user-context-panel__status-value">
              {format(parseISO(userInfo.createdAt), 'MMM yyyy')}
            </span>
          </div>
        </div>
      </div>

      {/* Appointments Summary */}
      {appointments.length > 0 && (
        <div className="user-context-panel__section">
          <h5 className="user-context-panel__section-title">Appointments</h5>
          <div className="user-context-panel__appointments-summary">
            <div className="user-context-panel__appointment-stat">
              <CalendarIcon size={20} />
              <div>
                <span className="user-context-panel__appointment-count">{upcomingAppointments.length}</span>
                <span className="user-context-panel__appointment-label">Upcoming</span>
              </div>
            </div>
            <div className="user-context-panel__appointment-stat">
              <CheckCircleIcon size={20} />
              <div>
                <span className="user-context-panel__appointment-count">{pastAppointments.length}</span>
                <span className="user-context-panel__appointment-label">Completed</span>
              </div>
            </div>
          </div>
          {upcomingAppointments.length > 0 && (
            <div className="user-context-panel__appointments-list">
              <h6>Upcoming</h6>
              {upcomingAppointments.slice(0, 3).map(apt => (
                <div
                  key={apt.id || (apt as any)._id}
                  className="user-context-panel__appointment-item"
                  onClick={() => {
                    const userType = userInfo.userType === 'client' ? 'client' : 'stylist';
                    navigate(`/${userType}/appointment/${publicAppointmentRef(apt)}`);
                  }}
                >
                  <div className="user-context-panel__appointment-date">
                    {format(parseISO(apt.dateTime), 'MMM d, h:mm a')}
                  </div>
                  <div className="user-context-panel__appointment-service">
                    {apt.service?.name || 'Service'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="user-context-panel__section">
        <h5 className="user-context-panel__section-title">Quick Actions</h5>
        <div className="user-context-panel__actions">
          {!userInfo.emailVerified && (
            <button 
              className="user-context-panel__action-btn"
              onClick={handleVerifyEmail}
              disabled={actionLoading === 'verify-email'}
            >
              {actionLoading === 'verify-email' ? 'Verifying...' : 'Verify Email'}
            </button>
          )}
          <button 
            className="user-context-panel__action-btn"
            onClick={handleViewProfile}
          >
            View Profile
          </button>
          {(userInfo.userType === 'client' || userInfo.userType === 'stylist') && (
            <button 
              className="user-context-panel__action-btn"
              onClick={handleViewAppointments}
            >
              View Appointments
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserContextPanel;

